import { forwardRef, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, INPUT_TRIGGER_CLS } from "@/lib/utils";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function parse(v?: string | null): { year: number; month: number } | null {
  if (!v) return null;
  const [y, m] = v.slice(0, 7).split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return null;
  return { year: y, month: m };
}

export const MonthPicker = forwardRef<HTMLButtonElement, {
  value: string | null | undefined;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}>(function MonthPicker({
  value, onChange, id, disabled, placeholder = "Pick a month", className,
}, ref) {
  const [open, setOpen] = useState(false);
  const selected = parse(value);
  const [viewYear, setViewYear] = useState(() => selected?.year ?? new Date().getFullYear());

  const label = selected ? `${MONTHS[selected.month - 1]} ${selected.year}` : placeholder;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (o) setViewYear(selected?.year ?? new Date().getFullYear());
        setOpen(o);
      }}
    >
      <PopoverTrigger
        ref={ref}
        id={id}
        disabled={disabled}
        className={cn(INPUT_TRIGGER_CLS, !selected && "text-muted-foreground", className)}
      >
        <span className="truncate text-left">{label}</span>
        <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="mb-2 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewYear((y) => y - 1)}
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium tabular-nums">{viewYear}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewYear((y) => y + 1)}
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((m, i) => {
            const isSelected = selected?.year === viewYear && selected?.month === i + 1;
            return (
              <Button
                key={m}
                type="button"
                variant={isSelected ? "default" : "ghost"}
                size="sm"
                className="font-normal"
                onClick={() => {
                  onChange(`${viewYear}-${String(i + 1).padStart(2, "0")}`);
                  setOpen(false);
                }}
              >
                {m}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
});
